/**
 * Public marketing + baseline authed pages that live inside the marketing
 * <Layout /> shell (top nav, footer, no sidebar). Kept as one <Route> tree so
 * the shared layout is applied consistently.
 */
import React from "react";
import { Route, Navigate, useLocation } from "react-router-dom";

import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Platform from "@/pages/Platform";
import JobSeekers from "@/pages/JobSeekers";
import Employers from "@/pages/Employers";
import Resources from "@/pages/Resources";
import Pricing from "@/pages/Pricing";
import Recruiting from "@/pages/Recruiting";
import EnterpriseInquiry from "@/pages/EnterpriseInquiry";
import RecruitingInquiry from "@/pages/RecruitingInquiry";
import RefundPolicy from "@/pages/RefundPolicy";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Cookies from "@/pages/Cookies";
import Subprocessors from "@/pages/Subprocessors";
import Dpa from "@/pages/Dpa";
import TrustCenter from "@/pages/TrustCenter";
import VerificationConsentPolicy from "@/pages/VerificationConsentPolicy";
import ForEmployersCompliance from "@/pages/ForEmployersCompliance";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Careers from "@/pages/Careers";
import JobDetail from "@/pages/JobDetail";
import PublicJobPage from "@/pages/PublicJobPage";
import PublicApplyPage from "@/pages/PublicApplyPage";
import CompanyProfile from "@/pages/CompanyProfile";
import Unsubscribe from "@/pages/Unsubscribe";
import PublicBooking from "@/pages/PublicBooking";
import Reschedule from "@/pages/Reschedule";
import AuthPage from "@/pages/AuthPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ConfirmEmailChange from "@/pages/ConfirmEmailChange";
import DashboardRedirect from "@/pages/DashboardRedirect";
import EmployerDashboard from "@/pages/EmployerDashboard";
import AcceptInvite from "@/pages/team/AcceptInvite";
import Inbox from "@/pages/Inbox";
import NotificationsPage from "@/pages/NotificationsPage";
import Settings from "@/pages/Settings";
import StatusPage from "@/pages/StatusPage";
import HowItWorks from "@/pages/public/HowItWorks";
import TalentPreviewPublic from "@/pages/public/TalentPreviewPublic";
import SuccessStories from "@/pages/public/SuccessStories";
import EmployerSignup from "@/pages/public/EmployerSignup";
import TalentSignup from "@/pages/public/TalentSignup";
import VerificationPage from "@/pages/VerificationPage";
import ThankYouEmployer from "@/pages/public/ThankYouEmployer";
import ThankYouTalent from "@/pages/public/ThankYouTalent";
import DataRequest, { DataRequestTracker } from "@/pages/DataRequest";
import PublicBadgePage from "@/pages/PublicBadgePage";
import BlogDetail from "@/pages/BlogDetail";

/** Legacy `/jobs/:id` deep-links redirect to `/careers/:id`. */
function JobDetailRedirect() {
    const { pathname } = useLocation();
    const id = pathname.split("/").pop();
    return <Navigate to={`/careers/${id}`} replace />;
}

/** Route the `:id` param to either the SEO-friendly public page (slug) or the
 *  legacy authenticated detail view (UUID). A slug always contains at least
 *  one hyphen — a UUID never does after the first dash. Prefer PublicJobPage
 *  by default so crawlers + humans see the same page. */
function JobDetailRouter() {
    return <PublicJobPage />;
}

/**
 * Returns the JSX subtree of <Route> nodes to be embedded inside a <Routes>.
 * Must be a function (not a component) because <Routes> requires plain
 * <Route> children — not user components that render Routes.
 */
export function publicRoutes() {
    return (
        <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/talent" element={<JobSeekers />} />
            <Route path="/job-seekers" element={<Navigate to="/talent" replace />} />
            <Route path="/employers" element={<Employers />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/blog" element={<Navigate to="/resources#blog" replace />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/enterprise" element={<EnterpriseInquiry />} />
            <Route path="/recruiting" element={<Recruiting />} />
            <Route path="/recruiting/inquiry" element={<RecruitingInquiry />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/subprocessors" element={<Subprocessors />} />
            <Route path="/dpa" element={<Dpa />} />
            <Route path="/trust-center" element={<TrustCenter />} />
            <Route path="/verification-consent-policy" element={<VerificationConsentPolicy />} />
            <Route path="/for-employers/compliance" element={<ForEmployersCompliance />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/success-stories" element={<SuccessStories />} />
            <Route path="/data-request" element={<DataRequest />} />
            <Route path="/data-request/track" element={<DataRequestTracker />} />
            <Route path="/badge/:token" element={<PublicBadgePage />} />
            <Route path="/employers/signup" element={<EmployerSignup />} />
            <Route path="/talent/signup" element={<TalentSignup />} />
            <Route path="/verification" element={<VerificationPage />} />
            <Route path="/thank-you-employer" element={<ThankYouEmployer />} />
            <Route path="/thank-you-talent" element={<ThankYouTalent />} />
            <Route path="/preview/:token" element={<TalentPreviewPublic />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/billing/cancel" element={<BillingCancel />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/jobs" element={<Careers />} />
            {/* Canonical SEO URL — matches sitemap.xml + Google JobPosting entries. */}
            <Route path="/jobs/:id" element={<JobDetailRouter />} />
            <Route path="/jobs/:slug/apply" element={<PublicApplyPage />} />
            <Route path="/careers/:id" element={<JobDetailRouter />} />
            <Route path="/careers/:slug/apply" element={<PublicApplyPage />} />
            <Route path="/companies/:slug" element={<CompanyProfile />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/book/:slug" element={<PublicBooking />} />
            <Route path="/reschedule/:token" element={<Reschedule />} />
            <Route path="/community" element={<Navigate to="/resources" replace />} />
            <Route path="/partners" element={<Navigate to="/about" replace />} />
            <Route path="/signup" element={<AuthPage mode="signup" />} />
            <Route path="/register" element={<AuthPage mode="signup" />} />
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
            <Route path="/dashboard" element={<DashboardRedirect />} />
            <Route path="/employer/dashboard-legacy" element={<EmployerDashboard />} />
            <Route path="/team/accept/:token" element={<AcceptInvite />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/status" element={<StatusPage />} />
        </Route>
    );
}
